import { useEffect, useRef } from "react";
import { useDebouncedCallback } from "use-debounce";
import { Table as AntdTable, TableProps as AntdTableProps } from "antd"
import { TablePaginationConfig } from "antd/es/table";
import { FilterValue } from "antd/es/table/interface";
import type { Key } from 'react';
import classNames from 'classnames';
import Button from '../../buttons/Button';

interface TableFilters {
  search?: string;
  [key: string]: any;
}

interface TableProps<T> extends AntdTableProps<T> {
  data: any
  defaultRowSelection?: Key[]
  onRowSelectionChange?: (selectedRowKeys: Key[], selectedRows: any[]) => void
  searchable?: boolean
  selectable?: boolean
  onRowClicked?: (row: any, event?: any) => void
  actionButtonText?: string
  onActionButtonClick?: () => void
  getCheckboxProps?: any
  onChange?: (pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: any) => void
  showSummary?: boolean;
  summaryData?: Record<string, any>;
  defaultFilters?: Record<string, any>;
}
const Table = ({
  className,
  columns,
  data = [],
  defaultRowSelection = [],
  onRowSelectionChange,
  searchable = true,
  selectable,
  onRowClicked,
  actionButtonText,
  onActionButtonClick = () => { },
  getCheckboxProps,
  scroll,
  onChange,
  showSummary = false,
  summaryData,
  defaultFilters = {},
  pagination: paginationProp,
  rowKey,
  ...props
}: TableProps<any>) => {
  const internalFilters = useRef<TableFilters>(defaultFilters)

  // Extract current page size from pagination prop
  const currentPageSize = paginationProp && typeof paginationProp === 'object'
    ? paginationProp.pageSize || 10
    : 10

  const debouncedSearch = useDebouncedCallback((search: string) => {
    internalFilters.current = {...internalFilters.current, search}
    // Use current page size instead of hardcoded 10
    onChange?.({current: 1, pageSize: currentPageSize}, internalFilters.current, null)
  }, 500)

  // Cleanup debounce on unmount
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

  const rowSelectionProps = selectable ? {
    rowSelection: {
      selectedRowKeys: defaultRowSelection,
      onChange(selectedRowKeys: Key[], selectedRows: any[]) {
        onRowSelectionChange?.(selectedRowKeys, selectedRows)
      },
      getCheckboxProps,
    }
  } : {}

  const onRowProps = onRowClicked ? {
    onRow: (record: any) => ({
      onClick: (event: any) => onRowClicked(record, event)
    })
  } : {}

  // Use pagination prop directly (parent controls it)
  const pagination = paginationProp || (data.length > 10 ? {} : false)

  return (
    <div className={className}>
      <div className="flex gap-x-2 items-baseline justify-between">
        <input
          type="text"
          onChange={e => debouncedSearch(e.target.value)}
          className={classNames("mb-4 rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50", { hidden: !searchable })}
          placeholder="Search"
          defaultValue={defaultFilters.search}
        />

        {actionButtonText && (
          <Button className='mb-4' onClick={onActionButtonClick}>{actionButtonText}</Button>
        )}
      </div>
      <AntdTable
        className="shadow border rounded-lg"
        columns={columns}
        rowKey={rowKey ?? ((record: any, index?: number) => record?._id ?? record?.id ?? index)}
        rowClassName={onRowClicked ? "hover:bg-gray-50 cursor-pointer" : ""}
        dataSource={data}
        sortDirections={['descend', 'ascend', null]}
        onChange={(pagination, filters, sorter) => {
          internalFilters.current = {...internalFilters.current, ...filters}
          onChange?.(pagination, internalFilters.current, sorter)
        }}
        {...onRowProps}
        {...rowSelectionProps}
        {...props}
        pagination={pagination}
        scroll={{...{x: 'max-content'}, ...scroll}}
        summary={showSummary && summaryData ? () => (
          <AntdTable.Summary fixed>
            <AntdTable.Summary.Row>
              {(columns || []).map((column: any, index) => (
                <AntdTable.Summary.Cell key={column.key || index} index={index}>
                  {summaryData[column.dataIndex]}
                </AntdTable.Summary.Cell>
              ))}
            </AntdTable.Summary.Row>
          </AntdTable.Summary>
        ) : undefined}
      />
    </div>
  )
}


export default Table
